import type { ViewDetailsResponse } from '@data-access/connect/tc-tcps';
import {
  getVisibleModelIds,
  SidePanelTabName,
  useApiServices,
  useCapabilityRegistryContext,
  useDataContext,
} from '@mobile-mapping/providers';
import { t } from '@trimble/i18n';
import { useAuth } from '@trimble/trimble-id-solidjs';
import { VerticalTab, ViewsTab } from '@trimble/ui-components/solidjs';
import { LoadState } from '@trimble/data-loading';
import { ViewsCapabilityProvider } from '@trimble/web3d-providers';
import { createMemo, Show } from 'solid-js';
import {
  type TcRegion,
  useConnectProject,
} from '@trimble/connect-project-providers';
import { useLoadView } from '../views/use-load-view';

interface ViewsSidePanelTabProps {
  onViewDetailsSelect?(view: ViewDetailsResponse): void;
  onCreateViewClick?(): void;
}

export function useViewsSidePanelTab(
  props: ViewsSidePanelTabProps
): VerticalTab<SidePanelTabName> {
  const { capabilityRegistry } = useCapabilityRegistryContext();
  const auth = useAuth();
  const { selectedRegion, selectedProject } = useConnectProject();
  const { tcTcpsApi, scanDataApi } = useApiServices();
  const { dataState } = useDataContext();
  const loadView = useLoadView();
  const viewsProvider = capabilityRegistry.get(ViewsCapabilityProvider);

  const withSelectedRegion = (
    run: (region: TcRegion) => Promise<void>
  ): Promise<void> => {
    const region = selectedRegion();
    if (!region) return Promise.reject(new Error('No region selected'));
    return run(region);
  };

  // wait for full model list load before views can trigger model loading.
  const viewsPrerequisitesMet = createMemo(() => {
    return (
      dataState.trajectory.state === LoadState.LoadComplete &&
      dataState.pointCloud.state === LoadState.LoadComplete &&
      dataState.tcFileFolder.state === LoadState.LoadComplete &&
      !!selectedRegion() &&
      !!selectedProject()
    );
  });

  const viewsHasErrors = createMemo(() => {
    return (
      dataState.trajectory.state === LoadState.Error ||
      dataState.pointCloud.state === LoadState.Error ||
      dataState.tcFileFolder.state === LoadState.Error
    );
  });

  const viewsIsLoading = createMemo(() => {
    if (!selectedProject()) return false;
    if (viewsHasErrors()) return false;
    if (viewsPrerequisitesMet()) return false;
    return true;
  });

  return {
    name: SidePanelTabName.Views,
    tooltip: {
      text: t('mobileMapping.views.title'),
      position: 'right',
    },
    icon: 'tc-icon-views',
    content: () => (
      <Show when={viewsProvider()}>
        <ViewsTab
          tcTcpsApi={tcTcpsApi}
          region={selectedRegion}
          projectId={() => selectedProject()?.id}
          defaultSortBy="createdOn"
          defaultSortDirection="desc"
          userEmail={() => auth().user?.email}
          getVisibleModelIds={() => getVisibleModelIds(dataState) ?? []}
          arePrerequisitesMet={viewsPrerequisitesMet}
          hasErrors={viewsHasErrors}
          isLoading={viewsIsLoading}
          deletePointCloudAssociationsForView={(projectId, viewId) =>
            withSelectedRegion((region) =>
              scanDataApi.deletePointCloudAssociationsForView(
                region,
                projectId,
                viewId
              )
            )
          }
          deleteReservedViewData={(viewId) =>
            withSelectedRegion((region) =>
              tcTcpsApi.deleteReservedViewData(region, viewId, 'numark')
            )
          }
          onCardClick={loadView}
          onViewDetailsSelect={props.onViewDetailsSelect}
          onCreateViewClick={props.onCreateViewClick}
        />
      </Show>
    ),
  };
}
